import { Box, BoxProps, CircularProgress, Link, Typography, styled, useTheme } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { LinkProps, Navigate, Link as RouteLink, useNavigate, useParams } from 'react-router-dom';

import Image from '../components/common/Image';

import DeerMap from '../assets/images/feral-scan/species/deer/startMapping.png';
import FeralCatMap from '../assets/images/feral-scan/species/feral-cat/startMapping.png';
import FeralFishMap from '../assets/images/feral-scan/species/feral-fish/startMapping.png';
import FeralGoatMap from '../assets/images/feral-scan/species/feral-goat/startMapping.png';
import FeralPigMap from '../assets/images/feral-scan/species/feral-pig/startMapping.png';
import FoxMap from '../assets/images/feral-scan/species/fox/startmapping.png';
import MouseMap from '../assets/images/feral-scan/species/mouse/startMapping.png';
import MynaMap from '../assets/images/feral-scan/species/myna/startMapping.png';
import NewPestsMap from '../assets/images/feral-scan/species/new-pests/startMapping.png';
import OtherPestsMap from '../assets/images/feral-scan/species/other-pests/startmapping.png';
import RabbitMap from '../assets/images/feral-scan/species/rabbit/startMapping.png';
import StarlingMap from '../assets/images/feral-scan/species/starling/startmapping.png';
import ToadMap from '../assets/images/feral-scan/species/toad/startMapping.png';
import WildDogMap from '../assets/images/feral-scan/species/wild-dog/startMapping.png';

import { Species } from '../api/dtos/dtos';
import Banksia from '../assets/images/feral-scan/Banksia.jpg';
import AppleStore from '../assets/images/feral-scan/badge-appstore.svg';
import GooglePlayStore from '../assets/images/feral-scan/badge-googleplay.png';
import CommonBanner from '../components/banners/CommonBanner';
import Center from '../components/common/Center';
import Hr from '../components/common/HorizontalRule';
import SpeciesContentRenderer from '../components/species-content/SpeciesContentRenderer';
import AuthContext from '../contexts/auth/AuthContext';
import { SpeciesContext } from '../contexts/data/SpeciesContext';
import useGetSpeciesContent from '../hooks/data/useGetSpeciesContent';
import RegisterPage from './RegisterPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import RequestDataAccessPage from './RequestDataAccessPage';
import FeedbackPage from './FeedbackPage';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import useWindowDimensions from '../hooks/meta/useWindowDimensions';
import useCustomHomePageLayers from '../hooks/map/useCustomHomePageLayers';

interface ISpeciesContentPageProps {
    page?: string;
}

const PROJECT_PLATYPUS_REGIONS = [{
    title: 'Project Platypus Landcare Network Boundary',
    fileName: 'Project Platypus Landcare Network boundary.kmz'
}];

const ContentWrapper = styled(Box)(() => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',

    "@media (min-width:1300px)": {
        flexDirection: 'row',
        position: 'relative'
    }
}));

const ContentColumn = styled(Box)(() => ({
    position: 'relative',
    display: 'block',
    flex: '2 0 auto',

    "@media (min-width: 1300px)": {
        flex: 2,
        width: '840px',
        overflow: 'hidden',
        order: 1,
    }
}));

const ContentMain = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const ContentDetails = styled(Box)(() => ({
    flex: '1 0 auto',
    order: '-1',

    "@media (min-width: 1300px)": {
        order: 1,
        background: '#e6e6e6',
        flex: 1,
        display: 'block'
    }
}));

const ContentDetailsWrapper = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    "@media (min-width:1300px)": {
        backgroundColor: 'initial',
    }
}));

const MapContainer = styled(Box)(() => ({
    position: 'relative',
    paddingTop: '5px',
    paddingBottom: '25px',
    marginBottom: '30px',
    width: '100%',
    background: 'radial-gradient(circle, rgb(183, 236, 255) 0%, rgb(0, 146, 200) 100%)',

    '@media (min-width: 1300px)': {
        flex: '1 0 auto',
        paddingBottom: '23px',
        marginBottom: '30px'
    }
}));

const MobileMapContainer = styled(Box)(() => ({
    position: 'relative',
    paddingTop: '5px',
    paddingBottom: '15px',
    marginBottom: '20px',
    width: '100%',
    background: 'radial-gradient(circle, rgb(183, 236, 255) 0%, rgb(0, 146, 200) 100%)',

    '@media (min-width: 1300px)': {
        flex: '1 0 auto',
        paddingBottom: '23px',
        marginBottom: '30px'
    }
}));

/*const MapManagerContainer = styled(Box)(() => ({
    position: 'relative',
    marginBottom: '25px',
    paddingTop: '5px',
    paddingBottom: '17px',
    boxShadow: 'inset 0 0 2px rgba(50, 50, 50, 0.6)',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgb(154, 192, 252) 0%, rgb(138, 180, 248) 100%)',

    '@media (min-width: 1300px)': {
        flex: '1 0 auto',
        paddingBottom: '25px',
        marginBottom: '30px'
    }
}));*/

const MapLink = styled(Box)(() => ({
    display: 'block',
    width: '45%',
    height: '100%',
    margin: '0 auto',
    position: 'relative',
    cursor: 'pointer',

    "@media (min-width: 100px)": {
        width: '80%'
    },
    
    "@media (min-width: 500px)": {
        width: '65%'
    },

    "@media (min-width: 992px)": {
        width: '30%'
    },

    "@media (min-width: 1300px)": {
        width: '80%'
    }
}));

const MobileMapLink = styled(Box)(() => ({
    width: '100%',
    height: '100%',
    margin: '0 auto',
    paddingLeft: '15px',
    paddingTop: '15px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textDecoration: 'none'
}));

const RecordSpecies = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    position: 'absolute',
    top: '100%',
    backgroundColor: theme.palette.primary.main,

    textDecoration: 'none',
    lineHeight: '3rem',
    fontSize: '1.25rem',
    color: 'white',
    borderRadius: '30px',

    boxShadow: 'inset 0 0 2px rgba(50, 50, 50, 0.6)',
    width: '100%',
    height: '48px',

    "&:hover": {
        WebkitBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        MozBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        boxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)'
    }
}));

const RabbitRecord = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: theme.palette.primary.main,

    lineHeight: '3rem',
    fontSize: '1rem',
    color: 'white',
    borderRadius: '30px',

    boxShadow: 'inset 0 0 2px rgba(50, 50, 50, 0.6)',
    width: '47%',
    height: '48px',

    "&, &:visited, &:hover, &:active": {
        textDecoration: 'none',
        color: 'white',
    },

    "&:hover": {
        boxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)'
    },
}));

const MobileRecordSpecies = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: theme.palette.primary.main,

    textDecoration: 'none',
    lineHeight: '3rem',
    fontSize: '1.25rem',
    color: 'white',
    borderRadius: '30px',
    marginRight: '10px',

    boxShadow: 'inset 0 0 2px rgba(50, 50, 50, 0.6)',
    height: '48px',
    width: '55%',

    "&:hover": {
        WebkitBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        MozBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        boxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)'
    },

    'a:-webkit-any-link': {
        textDecoration: 'none',
    }
}));

const MobileRecordRabbit = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: theme.palette.primary.main,

    textDecoration: 'none',
    lineHeight: '2.75rem',
    fontSize: '1.25rem',
    color: 'white',
    borderRadius: '30px',
    marginRight: '10px',

    boxShadow: 'inset 0 0 2px rgba(50, 50, 50, 0.6)',
    height: '42px',
    width: '75%',

    "&:hover": {
        WebkitBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        MozBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        boxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)'
    },

    'a:-webkit-any-link': {
        textDecoration: 'none',
    }
}));

const UserLink = styled(Box)<BoxProps & Partial<LinkProps>>(() => ({
    margin: '0 12px',
    color: 'black',

    textDecoration: 'none',

    borderRadius: '30px',

    backgroundColor: 'white',
    border: '1px solid',
    borderColor: '#d9d9d9',
    cursor: 'pointer',
    display: 'inline-block',
    padding: '12px 40px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    textAlign: 'center',

    "&:hover": {
        borderColor: '#ff6600'
    }
}));

const SpeciesStatisticsContainer = styled(Box)(() => ({
    display: 'block',
    minWidth: '200px',
    position: 'relative',
    marginBottom: '30px',

    "@media (min-width: 300px)": {
        width: '75%'
    },

    "@media (min-width: 700px)": {
        width: '35%'
    },

    "@media (min-width: 1300px)": {
        width: '75%'
    },
}));

const StyledRightHandContentBottom = styled(Box)(({ theme }) => ({
    width: '100%',
    padding: '0 24px',

    '& h3': {
        marginBottom: '0',
        color: theme.palette.primary.main,
        cursor: 'default',
        fontWeight: '500'
    },

    '& a': {
        display: 'flex',
        textDecoration: 'none',
        alignItems: 'center',
        color: theme.palette.secondary.main,
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },

    '& .icon-typography': {
        fontSize: '1rem',
    },

    '& .icon-typography-icon': {
        height: '24px',
        width: '24px',
        verticalAlign: 'middle',
        display: 'block',
    },

    '& .icon-typography-text': {
        verticalAlign: 'middle',
        display: 'block',
    }
}));

const DesktopDisplayContainer = styled(Box)(() => ({
    display: 'none',

    "@media (min-width: 1300px)": {
        display: 'block'
    },
}));

const MobileDisplayContainer = styled(Box)(() => ({
    display: 'block',

    "@media (min-width: 1300px)": {
        display: 'none'
    },
}));

const SpeciesContentPage: FC<ISpeciesContentPageProps> = ({
    page: pageProp
}) => {
    const theme = useTheme();

    const { species: speciesName, page: pageParam } = useParams();

    const { width } = useWindowDimensions();

    const { user, openLogin, logout } = useContext(AuthContext);
    const { species: { species } } = useContext(SpeciesContext);

    const navigate = useNavigate();

    const page = pageProp ?? pageParam;

    useCustomHomePageLayers({
        enabled: speciesName === 'deerscan' && page === 'deer_homepage_projectplatypus',
        regions: PROJECT_PLATYPUS_REGIONS
    });

    const isHome = useMemo(() => {
        if (page && page.includes("home")) {
            return true;
        }
        return false;
    }, [page]);

    if (!page) {
        return <Navigate replace to='/error' />;
    }

    const getIsComponent = (page: string) => {
        switch (page) {
            case "register":
                return true;
            case "forgotpassword":
                return true;
            case "requestdataaccess":
                return true;
            case "feedback":
                return true;
            default:
                return false;
        }
    }

    const getComponent = (page: string) => {
        switch (page) {
            case "register":
                return <RegisterPage />
            case "forgotpassword":
                return <ForgotPasswordPage />
            case "requestdataaccess":
                return <RequestDataAccessPage />
            case "feedback":
                return <FeedbackPage />
            default:
                return null;
        }
    }

    const { data, loading } = useGetSpeciesContent(!species || species == Species.None  ? undefined : {
        contentName: page,
        species: species,
        rightHandContentName: speciesName?.replace("scan", "")?.replace("alert", "") + '_righthandcontentbottom'
    }, {
        revalidateOnFocus: false,
        errorRetryCount: 0
    });


    const meta = useMemo(() => {
        return {
            pageContent: data?.pageContent ?? "<div></div>",
            pageLeadingTitle: data?.pageLeadingTitle,
            pageLeadingImageSrc: data?.pageLeadingImageSrc,
            bannerText: data?.contentBannerText,
            speciesName: data?.speciesName,
            totalRecords: data?.totalRecords,
            recentRecords: data?.recentRecords,
            totalGroups: data?.totalGroups,
            rightHandBottomContent: data?.rightHandContentBottom
        };
    }, [data]);

    const getSpeciesMapImage = (species: Species) => {
        switch (species) {
            case Species.WildDog:
                return WildDogMap;
            case Species.Deer:
                return DeerMap;
            case Species.Rabbit:
                return RabbitMap;
            case Species.OtherPests:
                return OtherPestsMap;
            case Species.FeralCat:
                return FeralCatMap;
            case Species.Fox:
                return FoxMap;
            case Species.FeralPig:
                return FeralPigMap;
            case Species.Myna:
                return MynaMap;
            case Species.Toad:
                return ToadMap;
            case Species.Mouse:
                return MouseMap;
            case Species.FeralGoat:
                return FeralGoatMap;
            case Species.FeralFish:
                return FeralFishMap;
            case Species.Starling:
                return StarlingMap;
            case Species.NewPests:
                return NewPestsMap;
            default:
                return WildDogMap;
        }
    }

    const getSpeciesPlurals = (species: Species) => {
        switch (species) {
            case Species.Rabbit:
                return "Rabbits";
            case Species.Fox:
                return "Foxes";
            case Species.Camel:
                return "Feral Camels";
            case Species.FeralPig:
                return "Feral Pigs";
            case Species.Myna:
                return "Myna Birds";
            case Species.WildDog:
                return "Wild Dogs";
            case Species.Toad:
                return "Toads";
            case Species.FeralGoat:
                return "Feral Goats";
            case Species.Starling:
                return "Starlings";
            case Species.Deer:
                return "Deer";
            case Species.FeralFish:
                return "Feral Fish";
            case Species.Mouse:
                return "Mice";
            case Species.FeralCat:
                return "Feral Cats";
            case Species.NewPests:
                return "New Pests";
            case Species.OtherPests:
                return "Other Pests";
            default:
                return "";
        }
    }

    return (
        <Box>
            {
                meta.pageLeadingImageSrc &&
                    <MobileDisplayContainer>
                        <Box 
                            position="relative" 
                            sx={{
                                zIndex: 99, 
                                background: 'radial-gradient(circle, rgb(183, 236, 255) 0%, rgb(0, 146, 200) 100%)',
                            }}
                        >
                            <Image width={'100%'} src={meta.pageLeadingImageSrc} />

                            <Box
                                position="absolute"
                                bottom={-10}
                                left="50%"
                                width="100%"
                                display="flex"
                                justifyContent="center"
                                sx={{
                                    transform: 'translateX(-50%)',
                                }}
                            >
                                <CommonBanner
                                    color={theme.palette.secondary.main}
                                    width="85%"
                                    text={meta.bannerText}
                                />
                            </Box>
                        </Box>
                    </MobileDisplayContainer>
            }
            <ContentWrapper>
                {/* -- Left Column -- */}
                <ContentColumn>
                    {/* -- TODO: Replace this with skeletal display */}
                    {
                        loading ?
                            <Center width='100%' height='90vh'>
                                <CircularProgress sx={{ margin: '0 auto' }} />
                            </Center> :
                            <>
                                {
                                    meta.pageLeadingImageSrc &&
                                        <DesktopDisplayContainer>
                                            <Box position="relative">
                                                <Image width="100%" src={meta.pageLeadingImageSrc} />

                                                <Box
                                                    position="absolute"
                                                    bottom={-10}
                                                    left="50%"
                                                    width="100%"
                                                    display="flex"
                                                    justifyContent="center"
                                                    sx={{
                                                        transform: 'translateX(-50%)',
                                                    }}
                                                >
                                                    <CommonBanner
                                                        color={theme.palette.secondary.main}
                                                        width="75%"
                                                        text={meta.bannerText}
                                                    />
                                                </Box>
                                            </Box>
                                        </DesktopDisplayContainer>
                                }

                                {/* -- Main Content -- */}
                                <ContentMain>
                                    { 
                                        getIsComponent(page) ? 
                                            getComponent(page) : 
                                            <>
                                                {
                                                    meta.pageLeadingTitle && !meta.pageLeadingImageSrc &&
                                                    <>
                                                        <Typography fontSize='1.6rem' marginBottom='16px'>{meta.pageLeadingTitle}</Typography>
                                                        <Hr />
                                                    </>
                                                }
                                                <SpeciesContentRenderer pageContent={meta.pageContent} /> 
                                            </>
                                    }
                                </ContentMain>
                            </>
                    }
                </ContentColumn>

                {/* -- Right Column -- */}
                <ContentDetails>
                    <ContentDetailsWrapper>

                        {(width <= 800 && !isHome) && (
                            <>
                                {/* -- Mobile Map Container -- */}
                                <MobileMapContainer>
                                    <MobileMapLink onClick={() => navigate(`/${speciesName}/map${species === Species.Rabbit ? '?mapMode=default' : ''}`)}>
                                        <Image width="35%" src={getSpeciesMapImage(species)} />
                                        {species == Species.Rabbit ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'end',
                                                    justifyContent: 'space-between',
                                                    width: "100%",
                                                    height: "100%"
                                                }}
                                            >
                                                <MobileRecordRabbit 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/${speciesName}/map?mapMode=default`);
                                                    }}
                                                >
                                                    Record {getSpeciesPlurals(species)}
                                                </MobileRecordRabbit>
                                                <br />
                                                <MobileRecordRabbit
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/${speciesName}/map?mapMode=rhdv`);
                                                    }}
                                                >
                                                    Report Disease
                                                </MobileRecordRabbit>
                                            </Box>
                                        ) : (
                                            <MobileRecordSpecies 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/${speciesName}/map`);
                                                }}
                                            >
                                                Record {getSpeciesPlurals(species)}
                                            </MobileRecordSpecies>
                                        )}
                                    </MobileMapLink>
                                </MobileMapContainer>
                            </>
                        )}

                        {(width > 800 || isHome) && (
                            <>
                                {/* -- Map Link -- */}
                                <MapContainer>
                                    <MapLink onClick={() => navigate(`/${speciesName}/map${species === Species.Rabbit ? '?mapMode=default' : ''}`)}>
                                        <Image width="100%" src={getSpeciesMapImage(species)} />
                                        {species === Species.Rabbit ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    position: 'absolute',
                                                }}
                                            >
                                                <RabbitRecord
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/${speciesName}/map?mapMode=default`);
                                                    }}
                                                >
                                                    Record {getSpeciesPlurals(species)}
                                                </RabbitRecord>
                                                <RabbitRecord
                                                    onClick={(e) => {
                                                        e.stopPropagation(); 
                                                        navigate(`/${speciesName}/map?mapMode=rhdv`);
                                                    }}
                                                >
                                                    Report Disease
                                                </RabbitRecord>
                                            </Box>
                                        ) : (
                                            <RecordSpecies
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/${speciesName}/map`);
                                                }}
                                            >
                                                Record {getSpeciesPlurals(species)}
                                            </RecordSpecies>
                                        )}
                                    </MapLink>
                                </MapContainer>

                                {/* -- User Profile Links -- */}
                                <Box sx={{ padding: '8px', display: 'flex', justifyContent: 'center' }}>
                                    {
                                        user ?
                                            <>
                                                <UserLink onClick={() => logout()}>Log out</UserLink>
                                                <UserLink component={RouteLink} to={`/${speciesName}/profile`}>My Profile</UserLink>
                                            </> :
                                            <>
                                                <UserLink onClick={() => openLogin()}>Log In</UserLink>
                                                <UserLink component={RouteLink} to={`/${speciesName}/register`}>Register</UserLink>
                                            </>
                                    }
                                </Box>

                                {/* -- Species Meta --  */}
                                <SpeciesStatisticsContainer>
                                    {
                                        species !== Species.NewPests && (
                                            <>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'space-between',
                                                        lineHeight: '40px'
                                                    }}
                                                >
                                                    <Typography lineHeight='40px' variant="h4" color="black">Total Records</Typography>
                                                    <Typography lineHeight='40px' fontSize="2.25rem">{meta.totalRecords ? new Intl.NumberFormat().format(meta.totalRecords) : 0}</Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'space-between',
                                                        lineHeight: '40px'
                                                    }}
                                                >
                                                    <Typography lineHeight='40px' variant="h4" color="black">Recent Records</Typography>
                                                    <Typography lineHeight='40px' fontSize="1.5rem">{meta.recentRecords ? new Intl.NumberFormat().format(meta.recentRecords) : 0}</Typography>
                                                </Box>
                                            </>
                                        )}
                                    {
                                        species === Species.WildDog && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                    lineHeight: '40px'
                                                }}
                                            >
                                                <Typography lineHeight='40px' variant="h4" color="black">Landholder Groups</Typography>
                                                <Typography lineHeight='40px' fontSize="1.5rem">{meta.totalGroups ? new Intl.NumberFormat().format(meta.totalGroups) : 0}</Typography>
                                            </Box>
                                        )}
                                </SpeciesStatisticsContainer>
                            </>
                        )}

                        <DesktopDisplayContainer>
                            {/* -- Management Map -- */}
                            {/*(species == Species.WildDog || species == Species.Deer) && 
                            (user?.roles?.includes("admin") || user?.roles?.includes("superadmin")) && (
                            <MapManagerContainer>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        zIndex: 2,
                                        textAlign: 'center',
                                        marginTop: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => navigate(`/management/${speciesName}/map`)}
                                >
                                    {
                                        species === Species.WildDog ?
                                        <Typography fontWeight='bold' color='#AC3F30' variant='h2'>Wild dog management programs</Typography> :
                                        <Typography fontWeight='bold' color='#947B67' variant='h2'>Deer management programs</Typography>
                                    }
                                </Box>
                                {
                                    species === Species.WildDog && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: '10px',
                                                zIndex: 3,
                                            }}
                                            onClick={() => navigate(`/management/${speciesName}/map`)}
                                        >
                                            <Image width="50%" src={WildDogMapManagementOverlay} />
                                        </Box>
                                )}
                                <MapLink component={RouteLink} to={`/management/${speciesName}/map`}>
                                    <Image width="100%" src={WildDogMapManagement} />
                                </MapLink>
                            </MapManagerContainer>
                        )*/}

                            {/* -- Store Links -- */}
                            <Box
                                sx={{
                                    width: '100%',
                                    padding: '8px 24px',
                                    display: 'block',
                                }}
                            >
                                <Typography color={theme.palette.primary.main} fontWeight={500} variant='h3' fontSize='1.4rem'>Download the app</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-around',
                                        padding: '16px 0'
                                    }}
                                >
                                    <Link
                                        href="https://apps.apple.com/au/app/id6756160183"
                                        target="_blank"
                                    >
                                        <Image height="50px" src={AppleStore} />
                                    </Link>
                                    <Link
                                        href="https://play.google.com/store/apps/details?id=com.invasiveanimals.feralscan"
                                        target="_blank"
                                    >
                                        <Image height="50px" src={GooglePlayStore} />
                                    </Link>
                                </Box>
                            </Box>

                            <StyledRightHandContentBottom>
                                { parse(DOMPurify.sanitize(meta.rightHandBottomContent ?? "")) }
                            </StyledRightHandContentBottom>

                            <Box sx={{ padding: '0 24px' }}>
                                <Image src={Banksia} />
                            </Box>
                        </DesktopDisplayContainer>

                    </ContentDetailsWrapper>
                </ContentDetails>
            </ContentWrapper>
            <MobileDisplayContainer>
                {/* -- Management Map -- */}
                {/*(species == Species.WildDog || species == Species.Deer) &&
                    (user?.roles?.includes("admin") || user?.roles?.includes("superadmin")) && (
                        <MapManagerContainer>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    zIndex: 2,
                                    textAlign: 'center',
                                    marginTop: '10px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/management/${speciesName}/map`)}
                            >
                                {
                                    species === Species.WildDog ?
                                        <Typography fontWeight='bold' color='#AC3F30' variant='h2'>Wild dog management programs</Typography> :
                                        <Typography fontWeight='bold' color='#947B67' variant='h2'>Deer management programs</Typography>
                                }
                            </Box>
                            {
                                species === Species.WildDog && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: '10px',
                                            zIndex: 3,
                                        }}
                                        onClick={() => navigate(`/management/${speciesName}/map`)}
                                    >
                                        <Image width="50%" src={WildDogMapManagementOverlay} />
                                    </Box>
                                )}
                            <MapLink component={RouteLink} to={`/management/${speciesName}/map`}>
                                <Image width="100%" src={WildDogMapManagement} />
                            </MapLink>
                        </MapManagerContainer>
                    )*/}

                {/* -- Store Links -- */}
                <Box
                    sx={{
                        width: '100%',
                        padding: '8px 24px',
                        display: 'block',
                    }}
                >
                    <Typography color={theme.palette.primary.main} fontWeight={500} variant='h3' fontSize='1.4rem'>Download the app</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-around',
                            padding: '16px 0'
                        }}
                    >
                        <Link
                            href="https://apps.apple.com/au/app/id6756160183"
                            target="_blank"
                        >
                            <Image height="50px" src={AppleStore} />
                        </Link>
                        <Link
                            href="https://play.google.com/store/apps/details?id=com.invasiveanimals.feralscan"
                            target="_blank"
                        >
                            <Image height="50px" src={GooglePlayStore} />
                        </Link>
                    </Box>
                </Box>

                <StyledRightHandContentBottom>
                    { parse(DOMPurify.sanitize(meta.rightHandBottomContent ?? "")) }
                </StyledRightHandContentBottom>

                <Box sx={{ padding: '0 24px' }}>
                    <Image src={Banksia} />
                </Box>
            </MobileDisplayContainer>
        </Box>
    )
}

export default SpeciesContentPage
